import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CubeGrid } from '../ui/CountingCubes';
import { QUESTIONS, ENCOURAGEMENTS } from '../../data/curriculum';
import { Check, X } from 'lucide-react';

const CompareGroups = ({ exercise, onAnswer }) => {
  const { left, right, question } = exercise;
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  useEffect(() => {
    setSelectedAnswer(null);
    setShowFeedback(false);
    setIsCorrect(false);
  }, [exercise]);

  const correctAnswer = question === 'more' 
    ? (left > right ? 'left' : 'right')
    : (left < right ? 'left' : 'right');

  const handleAnswer = (side) => {
    if (showFeedback) return;
    
    setSelectedAnswer(side);
    const correct = side === correctAnswer;
    setIsCorrect(correct);
    setShowFeedback(true);

    setTimeout(() => {
      onAnswer(correct);
    }, 1200);
  };

  const getOptionClass = (side) => {
    const baseClass = 'p-5 rounded-2xl border-3 transition-all duration-200 cursor-pointer flex-1 min-h-[120px] flex items-center justify-center';
    
    if (!showFeedback) {
      return `${baseClass} bg-card border-border hover:border-primary hover:scale-105 active:scale-95`;
    }
    if (side === correctAnswer) {
      return `${baseClass} bg-correct-light border-correct`;
    }
    if (side === selectedAnswer && !isCorrect) {
      return `${baseClass} bg-incorrect-light border-incorrect animate-shake`;
    }
    return `${baseClass} bg-card border-border opacity-50`;
  };

  const questionText = question === 'more' 
    ? QUESTIONS['compare-more']
    : QUESTIONS['compare-less'];

  const feedbackMessage = isCorrect 
    ? ENCOURAGEMENTS.correct[Math.floor(Math.random() * ENCOURAGEMENTS.correct.length)]
    : ENCOURAGEMENTS.incorrect[Math.floor(Math.random() * ENCOURAGEMENTS.incorrect.length)];

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] px-4">
      {/* Question */}
      <motion.h2 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-xl font-display font-bold text-foreground mb-8 text-center"
      >
        {questionText}
      </motion.h2>

      {/* Two Groups Side by Side */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="flex gap-4 justify-center w-full max-w-sm"
      >
        {/* Left Group */}
        <motion.button
          initial={{ scale: 0, x: -50 }}
          animate={{ scale: 1, x: 0 }}
          transition={{ delay: 0.3, type: 'spring' }}
          onClick={() => handleAnswer('left')}
          disabled={showFeedback}
          className={`${getOptionClass('left')} relative`}
        >
          {showFeedback && 'left' === correctAnswer && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-2 -right-2 w-6 h-6 bg-correct rounded-full flex items-center justify-center z-10"
            >
              <Check className="w-4 h-4 text-card" />
            </motion.span>
          )}
          {showFeedback && 'left' === selectedAnswer && !isCorrect && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-2 -right-2 w-6 h-6 bg-incorrect rounded-full flex items-center justify-center z-10"
            >
              <X className="w-4 h-4 text-card" />
            </motion.span>
          )}
          <CubeGrid count={left} size="md" animate={false} />
        </motion.button>

        {/* Right Group */}
        <motion.button
          initial={{ scale: 0, x: 50 }}
          animate={{ scale: 1, x: 0 }}
          transition={{ delay: 0.4, type: 'spring' }}
          onClick={() => handleAnswer('right')}
          disabled={showFeedback}
          className={`${getOptionClass('right')} relative`}
        >
          {showFeedback && 'right' === correctAnswer && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-2 -right-2 w-6 h-6 bg-correct rounded-full flex items-center justify-center z-10"
            >
              <Check className="w-4 h-4 text-card" />
            </motion.span>
          )}
          {showFeedback && 'right' === selectedAnswer && !isCorrect && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-2 -right-2 w-6 h-6 bg-incorrect rounded-full flex items-center justify-center z-10"
            >
              <X className="w-4 h-4 text-card" />
            </motion.span>
          )}
          <CubeGrid count={right} size="md" animate={false} />
        </motion.button>
      </motion.div>

      {/* Feedback */}
      <AnimatePresence>
        {showFeedback && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`mt-8 px-6 py-3 rounded-full font-display font-bold text-lg ${
              isCorrect 
                ? 'bg-correct-light text-success' 
                : 'bg-incorrect-light text-destructive'
            }`}
          >
            {feedbackMessage}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CompareGroups;
